/**
 * TabbedModal — generador reutilizable de modales con sidebar de tabs.
 *
 * ROBUSTO: toda la visibilidad se maneja con style.display inline.
 * No depende de clases CSS para show/hide — cero ambigüedad de cascada.
 * La clase 'active' es solo visual (highlight del tab), no funcional.
 *
 * Accesibilidad: los tabs son `<div>` por el CSS que ya los pinta, así que el
 * rol lo declaran los atributos ARIA y el teclado lo mueve `manejarTeclaEnTab`.
 * Sin ellos, un lector de pantalla anuncia "grupo" y el foco salta el sidebar
 * entero: el modal solo se podía operar con ratón.
 */
import { icon } from '../utils/icon.js';

/**
 * Solo el tab activo entra en el orden de tabulación (`tabindex=0`); los demás
 * se alcanzan con las flechas, no con Tab. Es el patrón de tablist de ARIA: Tab
 * entra al grupo y sale al panel, en vez de recorrer una por una las cinco
 * pestañas antes de llegar al primer campo.
 */
const TAB_ENFOCABLE = 0;
const TAB_NO_ENFOCABLE = -1;

export const TabbedModal = {
    /**
     * Genera el HTML del body interior del modal (sidebar + content).
     */
    render({ tabs, panels, defaultTab, modalId }) {
        const first = defaultTab || (tabs.length > 0 ? tabs[0].id : '');

        const tabsHtml = tabs
            .map(t => {
                const esActivo = t.id === first;
                return `
            <div class="modal-tab${esActivo ? ' active' : ''}" data-tab="${t.id}" data-modal="${modalId}"
                 role="tab" id="${modalId}-tab-${t.id}" aria-controls="${modalId}-panel-${t.id}"
                 aria-selected="${esActivo}" tabindex="${esActivo ? TAB_ENFOCABLE : TAB_NO_ENFOCABLE}">
                ${icon(t.icon)}
                <div class="tab-text">
                    <span class="tab-title">${t.title}</span>
                    <span class="tab-desc">${t.desc}</span>
                </div>
            </div>`;
            })
            .join('');

        const panelsHtml = tabs
            .map(
                t =>
                    `<div id="${modalId}-panel-${t.id}" class="tabbed-panel" role="tabpanel" tabindex="0"
                          aria-labelledby="${modalId}-tab-${t.id}"
                          style="display:${t.id === first ? 'block' : 'none'};">${panels[t.id] || ''}</div>`
            )
            .join('');

        return `
            <div class="modal-tabs" role="tablist" aria-orientation="vertical">${tabsHtml}</div>
            <div class="modal-content" style="padding:16px; flex:1; overflow-y:auto;">
                ${panelsHtml}
            </div>`;
    },

    /**
     * Cambia el tab activo. Todo via inline display — sin depender de CSS.
     *
     * `aria-selected` y `tabindex` viajan con la clase `active`: si solo
     * cambiara el color, un lector de pantalla seguiría anunciando como
     * seleccionada la pestaña anterior.
     *
     * @param {boolean} [moverFoco] — llevar el foco al tab activado. Lo pide la
     *   navegación por teclado; un click NO lo necesita (el foco ya está ahí) y
     *   forzarlo robaría el foco cuando el cambio lo dispara el código.
     */
    setTab(modalId, tabId, moverFoco = false) {
        const modal = document.getElementById(modalId);
        if (!modal) return;

        const tabs = modal.querySelectorAll('.modal-tab[data-modal]');
        const panels = modal.querySelectorAll('.tabbed-panel');

        for (let i = 0; i < tabs.length; i++) {
            tabs[i].classList.remove('active');
            tabs[i].setAttribute('aria-selected', 'false');
            tabs[i].setAttribute('tabindex', String(TAB_NO_ENFOCABLE));
        }
        for (let i = 0; i < panels.length; i++) panels[i].style.display = 'none';

        const activeTab = modal.querySelector(`.modal-tab[data-tab="${tabId}"]`);
        const activePanel = document.getElementById(`${modalId}-panel-${tabId}`);

        if (activeTab) {
            activeTab.classList.add('active');
            activeTab.setAttribute('aria-selected', 'true');
            activeTab.setAttribute('tabindex', String(TAB_ENFOCABLE));
            if (moverFoco) activeTab.focus();
        }
        if (activePanel) activePanel.style.display = 'block';
    },

    /**
     * Teclado sobre un tab: flechas para moverse, Home/End a los extremos,
     * Enter/Espacio para activar el que tenga el foco.
     *
     * Las flechas ACTIVAN al moverse (selección automática), que es lo correcto
     * cuando cambiar de pestaña no cuesta nada: el usuario oye el contenido
     * mientras recorre. Solo se consideran los tabs visibles — un modal que
     * oculta una pestaña por permisos (Lotowheel lo hace con «Acceso») no debe
     * mover el foco a algo que no está en pantalla.
     *
     * @returns {boolean} true si la tecla se consumió (el llamador hace
     *   preventDefault: las flechas si no scrollean el modal por debajo).
     */
    manejarTeclaEnTab(tabElement, tecla) {
        const modalId = tabElement?.dataset?.modal;
        if (!modalId) return false;
        const modal = document.getElementById(modalId);
        if (!modal) return false;

        if (tecla === 'Enter' || tecla === ' ') {
            this.setTab(modalId, tabElement.dataset.tab, true);
            return true;
        }

        const visibles = [...modal.querySelectorAll('.modal-tab[data-modal]')].filter(
            t => t.offsetParent !== null || t.style.display === ''
        );
        const actual = visibles.indexOf(tabElement);
        if (actual === -1) return false;

        let destino;
        // Arriba/abajo y izquierda/derecha valen las dos: el sidebar es vertical
        // en escritorio y horizontal en móvil (99-responsive.css ≤768px).
        if (tecla === 'ArrowDown' || tecla === 'ArrowRight') destino = (actual + 1) % visibles.length;
        else if (tecla === 'ArrowUp' || tecla === 'ArrowLeft')
            destino = (actual - 1 + visibles.length) % visibles.length;
        else if (tecla === 'Home') destino = 0;
        else if (tecla === 'End') destino = visibles.length - 1;
        else return false;

        this.setTab(modalId, visibles[destino].dataset.tab, true);
        return true;
    },
};
